import { useCallback } from 'react'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import FmdGoodOutlined from '@mui/icons-material/FmdGoodOutlined'
import Avatar from '@mui/material/Avatar'
import { useNavigate } from 'react-router-dom'
import { useFormatMessage } from '@/translations/useFormatMessage'
import type { LocationData, MenuSchedule } from '@/shared/api/location'
import { useAppDispatch, useAppSelector } from '@/store'
import { PATHS } from '@/router/paths'
import { sizing } from '@/styles/designTokens'
import {
	selectDineInTableName,
	selectSelectedMenuSchedule,
	selectCartItemCount,
	setMenuSchedule,
} from '@/store/ticketSlice'
import { toggleCartDrawer } from '@/store/uiSlice'
import { CartButton } from './CartButton'
import { TablePill } from './TablePill'
import { MenuSchedulePill } from './MenuSchedulePill'
import {
	HeaderRoot,
	ExpandedOuter,
	ExpandedContent,
	HeaderRow,
	BrandingBox,
	LocationName,
	LocationAddress,
	SchedulePillDesktopWrap,
	SchedulePillMobileWrap,
	SignInButton,
	SignInButtonText,
	SignInIconButton,
	CompactOuter,
	CompactContent,
} from './MenuHeader.styled'

function buildAddressLine(location: LocationData): string {
	let address = ''

	if (location.addressSelection === 'business') {
		const streetCity = [location.addressLine1, location.addressLine2, location.city].filter(
			Boolean,
		)
		const stateZip = [location.state, location.zip].filter(Boolean).join(' ')
		if (stateZip) streetCity.push(stateZip)
		address = streetCity.join(', ')
	} else if (location.addressSelection === 'custom') {
		address = location.customAddress ?? ''
	}

	const phone = location.oloContactPhone ?? ''
	if (address && phone) return `${address} | ${phone}`
	return address || phone
}

interface MenuHeaderProps {
	location?: LocationData
	schedules?: MenuSchedule[]
	isCompact?: boolean
}

export default function MenuHeader({ location, schedules, isCompact = false }: MenuHeaderProps) {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { t } = useFormatMessage()

	const selectedMenuSchedule = useAppSelector(selectSelectedMenuSchedule)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const authUser = useAppSelector((state) => state.auth.user)
	const cartItemCount = useAppSelector(selectCartItemCount)

	const handleCartClick = useCallback(() => dispatch(toggleCartDrawer()), [dispatch])

	const pillSchedules = schedules ?? location?.menuSchedules ?? []
	const activeSchedule =
		selectedMenuSchedule && pillSchedules.some((s) => s.ref === selectedMenuSchedule.ref)
			? selectedMenuSchedule
			: (pillSchedules[0] ?? null)

	const addressLine = location ? buildAddressLine(location) : ''

	const handleScheduleSelect = useCallback(
		(s: MenuSchedule) => dispatch(setMenuSchedule(s)),
		[dispatch],
	)

	const handleSignIn = useCallback(() => {
		if (location?.locationRef) void navigate(PATHS.accountEntry(location.locationRef))
	}, [navigate, location])

	if (!location) return null

	return (
		<HeaderRoot data-testid="menu-header">
			{/* ── Compact row (scrolled state): logo + schedule inline, no pill styling ── */}
			<CompactOuter isCompact={isCompact}>
				<CompactContent isCompact={isCompact}>
					<Avatar
						src={location.logoUrl ?? location.oloLogoUrl ?? undefined}
						alt={location.name ?? ''}
						sx={{
							flexShrink: 0,
							width: sizing.avatarCompact,
							height: sizing.avatarCompact,
						}}
					>
						{!(location.logoUrl ?? location.oloLogoUrl) && <FmdGoodOutlined />}
					</Avatar>

					{!dineInTableName && activeSchedule && (
						<MenuSchedulePill
							schedule={activeSchedule}
							schedules={pillSchedules}
							onSelect={handleScheduleSelect}
							compact
						/>
					)}
				</CompactContent>
			</CompactOuter>

			{/* ── Expanded content (default state) ── */}
			<ExpandedOuter isCompact={isCompact}>
				<ExpandedContent isCompact={isCompact}>
					{/* Row 1: branding + right actions */}
					<HeaderRow>
						<Avatar
							src={location.logoUrl ?? location.oloLogoUrl ?? undefined}
							alt={location.name ?? ''}
							sx={{
								flexShrink: 0,
								width: { xs: sizing.avatarMobile, lg: sizing.avatarDesktop },
								height: { xs: sizing.avatarMobile, lg: sizing.avatarDesktop },
							}}
						>
							{!(location.logoUrl ?? location.oloLogoUrl) && <FmdGoodOutlined />}
						</Avatar>

						<BrandingBox hasSignIn={location.signInPromptEnabled}>
							<LocationName>{location.name}</LocationName>
							<LocationAddress
								data-testid="location-address"
								title={addressLine || undefined}
							>
								{addressLine}
							</LocationAddress>
						</BrandingBox>

						{!dineInTableName && activeSchedule && (
							<SchedulePillDesktopWrap>
								<MenuSchedulePill
									schedule={activeSchedule}
									schedules={pillSchedules}
									onSelect={handleScheduleSelect}
								/>
							</SchedulePillDesktopWrap>
						)}

						{dineInTableName && <TablePill />}

						<CartButton itemCount={cartItemCount} onClick={handleCartClick} />

						{location.signInPromptEnabled && (
							<>
								<SignInButton
									data-testid="sign-in-pill"
									onClick={handleSignIn}
									aria-label={
										authUser
											? t('component:menuHeader:account:label')
											: t('component:menuHeader:signIn:label')
									}
								>
									<PersonOutlinedIcon sx={{ fontSize: sizing.navIcon }} />
									<SignInButtonText>
										{authUser
											? (authUser.name ??
												t('component:menuHeader:account:label'))
											: t('component:menuHeader:signIn:label')}
									</SignInButtonText>
								</SignInButton>

								<SignInIconButton
									data-testid="sign-in-icon"
									onClick={handleSignIn}
									aria-label={
										authUser
											? t('component:menuHeader:account:label')
											: t('component:menuHeader:signIn:label')
									}
								>
									<PersonOutlinedIcon />
								</SignInIconButton>
							</>
						)}
					</HeaderRow>

					{/* Row 2 (tablet/mobile only): full-width schedule pill */}
					{!dineInTableName && activeSchedule && (
						<SchedulePillMobileWrap>
							<MenuSchedulePill
								schedule={activeSchedule}
								schedules={pillSchedules}
								onSelect={handleScheduleSelect}
								fullWidth
							/>
						</SchedulePillMobileWrap>
					)}
				</ExpandedContent>
			</ExpandedOuter>
		</HeaderRoot>
	)
}
